/**
 * Shared utility for projecting 3D world positions to 2D screen coordinates
 *
 * Used by AircraftOverlay (selected aircraft) and AircraftLabelsOverlay (all aircraft).
 * Converts positions in feet (relative to map center) to pixel coordinates on screen.
 */

import mapboxgl from 'mapbox-gl'
import { FEET_TO_METERS } from '@/constants/units'

export interface ScreenPosition {
  x: number
  y: number
  visible: boolean
}

/**
 * Convert a 3D world position (feet from mapCenter) to 2D screen coordinates
 *
 * @param position - [x, y, z] in feet relative to map center
 * @param map - Mapbox GL map instance
 * @param mapCenter - Reference center point {lat, lng}
 * @param yOffset - Pixel offset above the projected point (default: -60)
 * @returns Screen position with visibility flag
 */
/**
 * Convert a 3D scene position (feet from mapCenter) to a geographic lng/lat.
 *
 * Inverse of the offset math in {@link project3DToScreen} — used to point the
 * Mapbox camera (`easeTo({ center })`) at a scene-placed object.
 *
 * @param position - [x, y, z] in feet relative to map center (y ignored)
 * @param mapCenter - Reference center point {lat, lng}
 * @returns [lng, lat]
 */
export function scenePositionToLngLat(
  position: [number, number, number],
  mapCenter: { lat: number; lng: number }
): [number, number] {
  const offsetX = position[0] * FEET_TO_METERS
  const offsetZ = position[2] * FEET_TO_METERS

  const refMercator = mapboxgl.MercatorCoordinate.fromLngLat([mapCenter.lng, mapCenter.lat], 0)
  const scale = refMercator.meterInMercatorCoordinateUnits()
  const newMercator = new mapboxgl.MercatorCoordinate(
    refMercator.x + offsetX * scale,
    refMercator.y + offsetZ * scale, // Scene +Z = south aligns with Mercator +Y = south
    refMercator.z
  )

  const lngLat = newMercator.toLngLat()
  return [lngLat.lng, lngLat.lat]
}

export function project3DToScreen(
  position: [number, number, number],
  map: mapboxgl.Map,
  mapCenter: { lat: number; lng: number },
  yOffset: number = -60
): ScreenPosition {
  try {
    // Convert position from feet to meters
    const offsetX = position[0] * FEET_TO_METERS
    const offsetZ = position[2] * FEET_TO_METERS

    // Get reference Mercator coordinate at map center
    const refMercator = mapboxgl.MercatorCoordinate.fromLngLat(
      [mapCenter.lng, mapCenter.lat],
      0
    )

    // Calculate new Mercator coordinate
    const scale = refMercator.meterInMercatorCoordinateUnits()
    const newMercator = new mapboxgl.MercatorCoordinate(
      refMercator.x + offsetX * scale,
      refMercator.y + offsetZ * scale, // Scene +Z = south aligns with Mercator +Y = south
      refMercator.z
    )

    // Convert to screen coordinates
    const lngLat = newMercator.toLngLat()
    const screenPoint = map.project(lngLat)

    // Check if within viewport (with 50px buffer)
    // Use clientWidth/clientHeight (CSS pixels) not width/height (device pixels)
    const canvas = map.getCanvas()
    const visible =
      screenPoint.x >= -50 &&
      screenPoint.x <= canvas.clientWidth + 50 &&
      screenPoint.y >= -50 &&
      screenPoint.y <= canvas.clientHeight + 50

    return {
      x: screenPoint.x,
      y: screenPoint.y + yOffset,
      visible,
    }
  } catch (error) {
    console.error('Error projecting position:', error)
    return { x: 0, y: 0, visible: false }
  }
}
